import React, { useState } from 'react'
import styled from 'styled-components'
import { useFetch } from 'use-http'

import { Box, Button, Card, Heading, Stack, Text } from '@myxplor/stardust'

import sortObjectsBy from '~/src/utils/sortObjectsBy'

import LoadingLoop from '~/src/components/LoadingLoop'

import UserModal from './UserModal'

const Table = styled.table`
  border-collapse: none;
  width: 100%;

  th {
    font-size: 14px;
    font-weight: bold;
    padding: 16px;
    text-align: left;
  }

  tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lowestEmphasis};
  }

  tbody > tr {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: ${({ theme }) => theme.colors.lowestEmphasis};
    }
  }

  td {
    font-size: 14px;
    padding: 16px;
    text-align: left;
  }
`

const printVendorList = (user: Papi.User) => {
  return user.vendors.length === 0
    ? '-'
    : sortObjectsBy(user.vendors, 'name')
        .map((vendor) => vendor.name)
        .join(', ')
}

const Users = () => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<Nullable<Papi.User>>(null)

  const userRequest = useFetch('/api/v1/users', {}, [])
  const vendorRequest = useFetch('/api/v1/vendors', {}, [])

  const onCancel = () => {
    setModalOpen(false)
    setSelectedUser(null)
  }

  const onSave = (user: Papi.User) => {
    setModalOpen(false)
    setSelectedUser(null)

    userRequest.get()
  }

  const onUserAdd = () => {
    setSelectedUser(null)
    setModalOpen(true)
  }

  const onUserEdit = (user: Papi.User) => {
    setSelectedUser(user)
    setModalOpen(true)
  }

  if (userRequest.loading || vendorRequest.loading) {
    return <LoadingLoop />
  }

  if (userRequest.error || vendorRequest.error) {
    return <div>Error...</div>
  }

  const users = sortObjectsBy(userRequest.data.data, 'display_name') as Papi.User[]
  const vendors = sortObjectsBy(vendorRequest.data.data, 'name') as Papi.Vendor[]

  return (
    <Stack space="small">
      <Box alignItems="center" display="flex" justifyContent="space-between" width={1}>
        <Heading level={4}>Users</Heading>
        <Button onClick={onUserAdd}>Add user</Button>
      </Box>
      <Text lineHeight={1.4}>
        {`Once adding users manually becomes painful we'll add the ability here. Until then, if you need to
        create or modify a user, ask a friendly engineer to make the changes in the database.`}
      </Text>
      <Card>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Identifier</th>
              <th>Admin</th>
              <th>Vendors</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} onClick={() => onUserEdit(user)}>
                <td>{user.id}</td>
                <td>{user.display_name}</td>
                <td>{user.identifier}</td>
                <td>{user.admin ? 'Yes' : 'No'}</td>
                <td>{printVendorList(user)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {isModalOpen && (
        <UserModal isOpen user={selectedUser} vendors={vendors} onCancel={onCancel} onSave={onSave} />
      )}
    </Stack>
  )
}

export default React.memo(Users)
